import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";
import { sendEmailVerificationCode } from "@/services";
import { Spinner } from "@/components/ui/spinner";
import { APP_VERSION } from "@/config/version";

const EmailVerification = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [confirmEmailError, setConfirmEmailError] = useState("");
  
  const emailTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const confirmEmailTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleBack = () => {
    navigate(-1);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Função para validar e-mail com debounce
  const validateEmailWithDebounce = (emailValue: string) => {
    // Limpar timeout anterior
    if (emailTimeoutRef.current) {
      clearTimeout(emailTimeoutRef.current);
    }

    // Limpar erro imediatamente
    setEmailError("");

    // Se o campo estiver vazio, não validar
    if (!emailValue) {
      return;
    }

    // Configurar timeout de 2 segundos
    emailTimeoutRef.current = setTimeout(() => {
      if (!validateEmail(emailValue)) {
        setEmailError("E-mail inválido");
      }
    }, 2000);
  };

  // Função para validar confirmação de e-mail com debounce
  const validateConfirmEmailWithDebounce = (confirmEmailValue: string) => {
    // Limpar timeout anterior
    if (confirmEmailTimeoutRef.current) {
      clearTimeout(confirmEmailTimeoutRef.current);
    }

    // Limpar erro imediatamente
    setConfirmEmailError("");

    // Se o campo estiver vazio, não validar
    if (!confirmEmailValue) {
      return;
    }

    // Configurar timeout de 2 segundos
    confirmEmailTimeoutRef.current = setTimeout(() => {
      if (email && confirmEmailValue && email !== confirmEmailValue) {
        setConfirmEmailError("Os e-mails não são iguais");
      }
    }, 2000);
  };

  // Cleanup dos timeouts
  useEffect(() => {
    return () => {
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
      if (confirmEmailTimeoutRef.current) {
        clearTimeout(confirmEmailTimeoutRef.current);
      }
    };
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    
    // Validar e-mail com debounce
    validateEmailWithDebounce(value);
    
    // Validar confirmação de e-mail com debounce se já estiver preenchida
    if (confirmEmail) {
      validateConfirmEmailWithDebounce(confirmEmail);
    }
  };

  const handleConfirmEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmEmail(value);
    
    // Validar confirmação de e-mail com debounce
    validateConfirmEmailWithDebounce(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Limpar timeouts pendentes
    if (emailTimeoutRef.current) {
      clearTimeout(emailTimeoutRef.current);
    }
    if (confirmEmailTimeoutRef.current) {
      clearTimeout(confirmEmailTimeoutRef.current);
    }
    
    // Resetar erros
    setEmailError("");
    setConfirmEmailError("");
    
    // Validações imediatas
    if (!email) {
      setEmailError("E-mail é obrigatório");
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError("E-mail inválido");
      return;
    }
    
    if (!confirmEmail) {
      setConfirmEmailError("Confirmação de e-mail é obrigatória");
      return;
    }
    
    if (email !== confirmEmail) {
      setConfirmEmailError("E-mails não coincidem");
      return;
    }

    setIsLoading(true);

    try {
      // Enviar código de verificação por e-mail
      const response = await sendEmailVerificationCode({
        email: email
      });

      // Se chegou até aqui, significa que retornou status 200 (sucesso)
      showToast({
        title: "Código enviado para seu e-mail!",
        type: "success"
      });
      
      // Navegar para tela de verificação de código
      navigate('/email-code-verification', {
        state: {
          email: email
        }
      });
    } catch (error: any) {
      console.error("Erro ao cadastrar e-mail:", error);
      showToast({
        title: "Erro ao cadastrar e-mail. Tente novamente.",
        type: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = email && confirmEmail && !emailError && !confirmEmailError && email === confirmEmail;

  // Tela de loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-gradient-primary">
        <div className="flex flex-col items-center justify-center min-h-screen px-5">
          <div className="w-full max-w-lg">
            <Card className="shadow-xl border-0 backdrop-blur-sm md:px-6 py-4 md:py-6 gap-0">
              <CardHeader className="pb-0">
                 {/* Logo */}
                 <div className="mb-6">
                   <img
                     src="/icon_oi_tv.svg"
                     alt="Oi TV"
                     className="h-12 w-auto object-contain mx-auto"
                   />
                 </div>

                {/* Loading Spinner */}
                <div className="flex flex-col items-center justify-center py-8">
                  <Spinner className="h-12 w-12 text-primary mb-4" />
                  <p className="text-lg font-medium text-gray-800">
                    Enviando código
                  </p>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-gradient-primary">
      {/* Main Content */}
      <div className="flex flex-col items-center justify-center min-h-screen px-5">
        <div className="w-full max-w-lg">
          <Card className="shadow-xl border-0 backdrop-blur-sm md:px-6 py-4 md:py-6 gap-0">
            <CardHeader className="pb-0">
              {/* Header with Back Button and Logo */}
              <div className="mb-6 flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <ArrowLeft className="h-6 w-6 text-gray-600" />
                </button>
                <img
                  src="/icon_oi_tv.svg"
                  alt="Oi TV"
                  className="h-12 w-auto object-contain"
                />
                <div className="w-10"></div> {/* Spacer para manter o logo centralizado */}
              </div>

              {/* Title */}
              <div className="space-y-2">
                <h1 className="text-2xl font-bold">
                  Para continuar, insira seu e-mail.
                </h1>
                <p className="text-sm text-gray-600">
                  É para ele que enviaremos sua fatura digital a partir de agora.
                </p>
                <p className="text-xs text-gray-500">
                  O código de confirmação será enviado para o e-mail inserido.
                </p>
              </div>
            </CardHeader>

            <CardContent className="pt-6">
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {/* Email Input */}
                  <div>
                    <Input
                      type="email"
                      placeholder="E-mail"
                      value={email}
                      onChange={handleEmailChange}
                      className={`${emailError ? "border-red-500" : ""}`}
                      disabled={isLoading}
                    />
                    {emailError && (
                      <p className="text-red-500 text-xs mt-1">{emailError}</p>
                    )}
                  </div>

                  {/* Confirm Email Input */}
                  <div>
                    <Input
                      type="email"
                      placeholder="Repita seu e-mail"
                      value={confirmEmail}
                      onChange={handleConfirmEmailChange}
                      className={`${confirmEmailError ? "border-red-500" : ""}`}
                      disabled={isLoading}
                    />
                    {confirmEmailError && (
                      <div className="flex items-center gap-2 text-red-500 text-xs mt-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span>{confirmEmailError}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-green-600"
                    disabled={!isFormValid || isLoading}
                  >
                    {isLoading ? "Enviando..." : "Continuar"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Footer Info */}
          <div className="flex items-center justify-start mt-6 gap-2">
            <img
              src="/important_icon.svg"
              alt="Informação importante"
              className="w-5 h-5"
            />
            <p className="text-xs text-white text-left">
              Se quiser receber sua fatura pelo correio, acesse nosso canal de atendimento 10631
            </p>
          </div>

          <div className="flex justify-center items-center mt-4">
            <span className="text-xs text-white">{APP_VERSION}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
